import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { forwardRef, KeyboardEventHandler, useState } from "react";
import { Input } from "rsuite";
import { useSearchParams } from "react-router-dom";
import useUserSearch from "features/search/hooks/useNavSearchUsers";
import useAppNavigation from "hooks/useAppNavigation";
import SearchIcon from "components/Elements/Icons/SearchIcon";
import styled from "styled-components";
import NavBarResults from "./NavBarResults";

const SearchTrigger = styled.button`
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0.8;
  position: relative;
  background-color: #f5f5f5;
  width: 40px;
  height: 40px;
  border-radius: 4px;

  svg {
    color: var(--color-light-gray);
    height: 24px;
  }

  &:hover {
    opacity: 1;
  }
`;

const SearchBarContainer = styled.div`
  position: absolute;
  top: 0;
  left: -528px;
  z-index: 9;
  width: 520px;
  height: 41px;
`;

const InputContainer = styled.div<{ showResults: boolean }>`
  border: solid thin #e2e2e2;
  background-color: #f5f5f5;
  color: #575757;
  cursor: text;
  display: flex;
  position: relative;
  transition: border-color 0.15s ease-in-out;
  width: 100%;
  height: 100%;
  align-items: center;
  border-bottom-right-radius: ${(props) => (props.showResults ? "0" : "6")}px;
  border-bottom-left-radius: ${(props) => (props.showResults ? "0" : "6")}px;
  border-top-right-radius: 6px;
  border-top-left-radius: 6px;
  padding: 0 5px;

  .hnc-search {
    border: none;
    border-radius: 0px;
    font-size: 14px;
    color: #222222;
    background-color: transparent;

    &:focus {
      outline-color: transparent;
    }

    &::placeholder {
      color: #222222;
      font-size: 14px;
    }
  }
`;

const InputLeft = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 41px;
  height: 100%;
`;

const InputRight = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 41px;
  height: 100%;
`;

const SearchIconButton = styled.button`
  background-color: #243841;
  height: 32px;
  width: 32px;
  border-radius: 6px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ExitIconButton = styled.button`
  background-color: transparent;
  height: 32px;
  width: 32px;
  border-radius: 6px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const NavbarSearch = forwardRef((props: any, ref: any) => {
  const [searchParams] = useSearchParams();

  // grab search value from the url
  const urlSearchValue = searchParams.get("q");

  // tracks if the search results container show be shown/hidden
  const [showResults, setShowResults] = useState<boolean>(false);
  const [showSearch, setShowSearch] = useState<boolean>(false);

  // get app navigation
  const { navToSearchResults } = useAppNavigation();

  // get user search
  const { isFetching, results, searchValue, onSearch, clearState } =
    useUserSearch(urlSearchValue || "", 5);

  /**
   * Show user results
   */
  const searchClickHandler = () => {
    if (results.length > 0) {
      setShowResults(true);
    }
  };

  const clearResults = () => {
    // clear user search
    clearState();

    // clear the input field search value
    if (ref && ref.current) {
      // eslint-disable-next-line no-param-reassign
      ref.current.value = "";
    }
  };

  /**
   * Updates the the show/hide state of the search results
   * container. If a value is present, the container is shown
   * and a async req is made for results
   * @param val Search value
   */
  const searchChangeHandler = (val: string) => {
    // perform search
    onSearch(val);

    if (val.trim() !== "") {
      // show result container
      setShowResults(true);
    } else {
      // hide results container
      setShowResults(false);
    }
  };

  const hideAll = () => {
    setShowResults(false);
    setShowSearch(false);
  };
  /**
   * Listens for keydown on search field
   * If the key matches "Enter",
   * navigate the user to the search results page
   * @param e
   */
  const onKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === "Enter" && searchValue !== "") {
      navToSearchResults(searchValue);
      hideAll();
      ref.current.blur();
    }
  };

  /**
   * Listens for search focus
   * Displays current search results if the
   * search query value is not empty
   */
  const onFocus = () => {
    if (urlSearchValue && urlSearchValue !== "") {
      setShowResults(true);
    }
  };

  if (showSearch) {
    return (
      <SearchBarContainer>
        <InputContainer showResults={showResults}>
          <InputLeft>
            <SearchIconButton
              onClick={() => {
                if (searchValue !== "") navToSearchResults(searchValue);
              }}
            >
              <SearchIcon size={16} color="white" />
            </SearchIconButton>
          </InputLeft>
          <Input
            className="hnc-search"
            ref={ref}
            onClick={searchClickHandler}
            onChange={searchChangeHandler}
            placeholder="Search for users."
            onKeyDown={onKeyDown}
            value={searchValue}
            onFocus={onFocus}
          />

          <InputRight
            onClick={() => {
              hideAll();
              clearResults();
            }}
          >
            <ExitIconButton>
              <FontAwesomeIcon icon={faTimes} />
            </ExitIconButton>
          </InputRight>
        </InputContainer>

        {showResults && (
          <NavBarResults
            searchValue={searchValue}
            clearResults={() => setShowResults(false)}
            setShowResults={setShowResults}
            searchResults={results}
            isFetching={isFetching}
          />
        )}
      </SearchBarContainer>
    );
  }
  return (
    <SearchTrigger onClick={() => setShowSearch(true)}>
      <SearchIcon color="#8e8e8e" size={16} />
    </SearchTrigger>
  );
});

export default NavbarSearch;
